import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { POPUP_PAGES } from "@/components/SitePopup";

const inp = "mt-1 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm";
const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromLocal = (v) => (v ? new Date(v).toISOString() : null);
const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg bg-muted px-2 py-2 text-center">
    <p data-testid={testid} className="font-display text-xl font-bold text-primary">{value}</p>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
  </div>
);

const PopupStats = ({ s, hasButton }) => {
  const rate = s.views ? Math.round((s.clicks / s.views) * 100) : 0;
  return (
    <div className="rounded-xl border border-border p-3" data-testid="popup-stats">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Visitors saw it" value={s.views} testid="popup-stat-views" />
        <Stat label={hasButton ? `Button taps · ${rate}%` : "Button taps"} value={s.clicks} testid="popup-stat-clicks" />
        <Stat label="Closed" value={s.dismissals} testid="popup-stat-dismissals" />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Counts each visitor's browser once. Counts start over when you change the wording, photo or button.{s.last_at ? ` Last activity ${new Date(s.last_at).toLocaleString()}.` : ""}</p>
    </div>
  );
};

const Lbl = ({ l, children }) => (<div><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{l}</label>{children}</div>);

export const PopupAdminTab = () => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/popup").then((r) => setF(r.data)).catch(() => toast.error("Could not load popup")); }, []);
  if (!f) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const upd = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const togglePage = (p) => setF((prev) => ({ ...prev, pages: prev.pages.includes(p) ? prev.pages.filter((x) => x !== p) : [...prev.pages, p] }));

  const save = async () => {
    setBusy(true);
    try {
      const keys = ["enabled", "title", "body", "image", "button_label", "button_url", "pages", "start_at", "end_at"];
      const { data } = await api.put("/admin/popup", Object.fromEntries(keys.map((k) => [k, f[k]])));
      setF(data); toast.success(data.live ? "Popup saved and live" : "Popup saved (not showing right now)");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? (d[0]?.msg || "").replace(/^Value error, /, "") : d || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-4" data-testid="popup-admin">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-primary">Website Popup</h3>
        <span data-testid="popup-live-status" className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${f.live ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>{f.live ? "Showing now" : "Not showing"}</span>
      </div>
      {f.stats && <PopupStats s={f.stats} hasButton={!!(f.button_label && f.button_url)} />}
      <p className="text-xs text-muted-foreground">Shows on the main event website only, never in the game. Each visitor sees it once; editing the wording, photo or button shows it again to everyone.</p>
      <label className="flex items-center gap-2 text-sm font-semibold"><input data-testid="popup-enabled" type="checkbox" checked={f.enabled} onChange={(e) => upd("enabled", e.target.checked)} /> Popup turned on</label>
      <Lbl l="Title"><input data-testid="popup-title" value={f.title} onChange={(e) => upd("title", e.target.value)} className={inp} maxLength={200} /></Lbl>
      <Lbl l="Message"><textarea data-testid="popup-body" value={f.body} onChange={(e) => upd("body", e.target.value)} rows={6} maxLength={5000} className="mt-1 w-full rounded-lg border border-border bg-background p-3 text-sm" /></Lbl>
      <ImageUpload label="Photo (optional)" testid="popup-image" value={f.image} onChange={(v) => upd("image", v)} />
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="min-w-0 flex-1"><Lbl l="Button text (optional)"><input data-testid="popup-button-label" value={f.button_label} onChange={(e) => upd("button_label", e.target.value)} placeholder="Buy Tickets" className={inp} maxLength={60} /></Lbl></div>
        <div className="min-w-0 flex-1"><Lbl l="Button link"><input data-testid="popup-button-url" value={f.button_url} onChange={(e) => upd("button_url", e.target.value)} placeholder="/shop or https://…" className={inp} /></Lbl></div>
      </div>
      <Lbl l="Show on these pages">
        <div className="mt-1 grid grid-cols-2 gap-1.5">
          {POPUP_PAGES.map(([p, label]) => (
            <label key={p} className="flex items-center gap-2 rounded-lg border border-border px-2 py-1.5 text-sm">
              <input data-testid={`popup-page-${label.toLowerCase().replace(/\s+/g, "-")}`} type="checkbox" checked={f.pages.includes(p)} onChange={() => togglePage(p)} /> {label}
            </label>
          ))}
        </div>
      </Lbl>
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="min-w-0 flex-1"><Lbl l="Start (optional)"><input data-testid="popup-start" type="datetime-local" value={toLocal(f.start_at)} onChange={(e) => upd("start_at", fromLocal(e.target.value))} className={inp} /></Lbl></div>
        <div className="min-w-0 flex-1"><Lbl l="Stop showing at"><input data-testid="popup-end" type="datetime-local" value={toLocal(f.end_at)} onChange={(e) => upd("end_at", fromLocal(e.target.value))} className={inp} /></Lbl></div>
      </div>
      <p className="text-xs text-muted-foreground">Times are in your device's time zone. Leave Start blank to begin as soon as you save, or Stop blank to run until you turn it off.</p>
      <button data-testid="popup-save" onClick={save} disabled={busy} className="h-11 w-full rounded-full bg-primary text-sm font-bold text-primary-foreground disabled:opacity-60">{busy ? "Saving…" : "Save Popup"}</button>
    </div>
  );
};
